import Markdown from 'markdown-to-jsx';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getChatbotAnalyticsControlData,
  selectChatbotAnalyticsBusinessId,
  unSelectChatbotAnalyticsBusinessId,
} from '../../store/slices/chatbotAnalyticsSlice';
import { getChatbotBusinessesAnalyticsThunk } from '../../store/thunks/chatbotAnalyticsThunks';
import Icon from '@mdi/react';
import { mdiThumbDownOutline, mdiThumbUpOutline } from '@mdi/js';
import { useTheme } from '@material-ui/core';
import Button from 'components/Button/Button';

// --- Styles (inline style objects) -----------------------------------------
const styles = {
  app: {
    fontFamily:
      'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
    background: '#0b1020',
    color: '#e7eaf6',
    minHeight: '100vh',
    padding: 24,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  title: { fontSize: 22, fontWeight: 700 },
  searchRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    marginBottom: 16,
  },
  input: {
    background: '#121933',
    color: '#e7eaf6',
    border: '1px solid #27325a',
    outline: 'none',
    padding: '10px 12px',
    borderRadius: 10,
    minWidth: 260,
  },
  card: {
    background: '#0f1630',
    border: '1px solid #223058',
    borderRadius: 14,
    padding: 14,
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 14px',
    borderRadius: 12,
    cursor: 'pointer',
    background: '#0e1530',
    border: '1px solid #1e2a52',
  },
  userRowMeta: { display: 'flex', gap: 10, alignItems: 'baseline' },
  badge: {
    fontSize: 12,
    padding: '2px 8px',
    borderRadius: 999,
    background: '#18214a',
    border: '1px solid #293a76',
  },
  chevron: {
    transition: 'transform 150ms ease',
    marginLeft: 8,
    fontSize: 16,
    opacity: 0.9,
  },
  list: { display: 'grid', gap: 12 },
  panel: { padding: 10, display: 'grid', gap: 10 },
  qaItem: {
    background: '#0c132a',
    border: '1px solid #1c2950',
    borderRadius: 12,
    padding: 12,
    display: 'grid',
    gap: 8,
  },
  row: { display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' },
  label: { fontSize: 12, opacity: 0.8 },
  mono: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  msgUser: { whiteSpace: 'pre-wrap', lineHeight: 1.4 },
  msgAssistant: {
    whiteSpace: 'pre-wrap',
    lineHeight: 1.45,
    opacity: 0.95,
    borderTop: '1px dashed #223058',
    paddingTop: 8,
  },
  hr: { height: 1, background: '#1a2548', border: 0, margin: '10px 0' },
  subtle: { opacity: 0.8 },
};

export function ChatbotBusinessAnalytics() {
  const theme = useTheme();

  const dispatch = useDispatch();
  const chatbotAnalyticsControlData = useSelector(
    getChatbotAnalyticsControlData
  );
  const { businessId } = useParams();

  useEffect(() => {
    if (!businessId) return;
    if (chatbotAnalyticsControlData.selectedBusinessId === undefined) {
      console.log('Selecting business from URL:', businessId);
      dispatch(selectChatbotAnalyticsBusinessId(businessId));
    }
  }, [businessId, chatbotAnalyticsControlData.selectedBusinessId]);

  // Unselect ONLY on unmount
  useEffect(() => {
    return () => {
      console.log('ChatbotBusinessAnalytics destroyed — unselecting');
      dispatch(unSelectChatbotAnalyticsBusinessId());
    };
  }, [dispatch]);

  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');

  const yearMonth = `${year}-${month}`; // ex. '2025-12'; // backend fetches via month
  console.log('Fetch analytics for this month:');
  console.log(yearMonth);

  const analytics = chatbotAnalyticsControlData.chatbotAnalytics;
  const requestedRef = useRef(new Set());

  // FETCH BUSINESSES chatbot analytics
  useEffect(() => {
    const key = `${businessId}:${yearMonth}`;
    if (requestedRef.current.has(key)) return;

    if (!analytics) {
      console.log(
        'ei olllu analytikkaa haettu viel tälle yritykselle jote hae'
      );
      requestedRef.current.add(key);
      dispatch(getChatbotBusinessesAnalyticsThunk({ yearMonth, businessId }));
    }
  }, [chatbotAnalyticsControlData]);
  const [isOpen, setOpen] = useState(new Set());
  const [openQA, setOpenQA] = useState(new Set());
  const toggleUser = (userId) =>
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  const toggleQA = (userId, index) =>
    setOpenQA((prev) => {
      const k = `${userId}:${index}`;
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const isoToLocal = (iso) =>
    new Date(iso).toLocaleString(undefined, { hour12: false });

  function downloadCsvFromData(data) {
    if (!Array.isArray(data)) {
      throw new Error('downloadCsvFromData: expected an array');
    }

    const headers = ['nimi', 'userMessage', 'assistantMessage', 'timeStamp'];
    const rows = [headers];

    // Build rows: one row per kysymysVastaus entry (duplicate 'nimi' for each)
    data.forEach((item) => {
      const name = item && item.nimi != null ? String(item.nimi) : '';
      const kvs = Array.isArray(item && item.kysymysVastaus)
        ? item.kysymysVastaus
        : [];

      if (kvs.length === 0) {
        // If no Q/A entries, still output a row for the name
        rows.push([name, '', '', '']);
        return;
      }

      kvs.forEach((qa) => {
        rows.push([
          name,
          qa && qa.userMessage != null ? String(qa.userMessage) : '',
          qa && qa.assistantMessage != null ? String(qa.assistantMessage) : '',
          qa && qa.timeStamp != null ? String(qa.timeStamp) : '',
        ]);
      });
    });

    // CSV-safe cell: normalize newlines, escape quotes, wrap in quotes
    const esc = (val) => {
      const s = String(val == null ? '' : val)
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/"/g, '""');
      return `"${s}"`;
    };

    const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n');

    // Add UTF-8 BOM for Excel compatibility
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'keskustelut.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  const generateCsv = () => {
    const attributesWeWant = analytics.map((analytic) => {
      const questionAndAnswer = analytic.data.map((d) => {
        return {
          userMessage: d.userMessage,
          assistantMessage: d.assistantMessage,
          timeStamp: isoToLocal(d.startedAtIso),
        };
      });

      return {
        nimi: analytic.user.name,
        kysymysVastaus: questionAndAnswer,
      };
    });

    downloadCsvFromData(attributesWeWant);
  };

  if (!analytics) return <p>Loading</p>;

  return (
    <div style={styles.app}>
      <div style={styles.header}>
        <div style={styles.title}>Nerobotin käyttö analytiikka</div>
        <Button onClick={generateCsv} color="primary">
          Luo CSV tiedosto käyttö datasta
        </Button>
      </div>

      <div style={styles.list}>
        {analytics.map((analytic) => {
          const open = isOpen.has(analytic.user.userId); // ← per-user flag
          const chevron = open ? '▾' : '▸';
          return (
            <div key={analytic.user.userId} style={styles.card}>
              <div
                style={styles.userRow}
                onClick={() => toggleUser(analytic.user.userId)}
              >
                <div style={styles.userRowMeta}>
                  <span style={{ ...styles.mono, fontSize: 14 }}>
                    {analytic.user.name}
                  </span>
                  <span style={styles.badge}>
                    {analytic.data.length} messages
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: 10, fontSize: 13, opacity: 0.9 }}>
                    total cost: {Number(analytic.totalCost).toFixed(3)}€
                  </span>
                  <span style={styles.chevron}>{chevron}</span>
                </div>
              </div>

              {open && (
                <div style={styles.panel}>
                  {analytic.data.map((r, index) => {
                    const qaKey = `${analytic.user.userId}:${index}`;
                    const qaOpen = openQA.has(qaKey);
                    const chevron = qaOpen ? '▾' : '▸';
                    return (
                      <div key={index} style={styles.qaItem}>
                        <div
                          style={{ ...styles.row, cursor: 'pointer' }}
                          onClick={() => toggleQA(analytic.user.userId, index)}
                        >
                          <span style={styles.label}>Started</span>
                          <span style={{ ...styles.mono }}>
                            {isoToLocal(r.startedAtIso)}
                          </span>

                          <span style={styles.badge}>
                            Projekti: {r.projectId}
                          </span>
                          <span style={styles.badge}>
                            Käytetyt tokenit: {r.totalTokensUsed}
                          </span>
                          <span style={styles.badge}>
                            AI Hinta:
                            {Number(r.costEstimate).toFixed(3)}€
                          </span>
                          <span style={styles.badge}>
                            OpenSearch eli Knowledgebase Hinta:
                            {Number(
                              r?.openSearchEstimate?.totalCostEstimate
                            ).toFixed(3)}
                            €
                          </span>
                          <span style={styles.chevron}>{chevron}</span>
                        </div>

                        {qaOpen && (
                          <>
                            <div style={styles.msgUser}>
                              <strong>{analytic.user.name}:</strong>{' '}
                              {r.userMessage}
                            </div>
                            <div style={styles.msgAssistant}>
                              <strong>Nerobotin Vastaus:</strong>
                              <Markdown>{r.assistantMessage}</Markdown>

                              {r.feedback ? (
                                <div>
                                  <p>Käyttäjän palaute:</p>
                                  {r.feedback === 'up' ? (
                                    <button
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: theme.palette.success.main,
                                      }}
                                    >
                                      <Icon
                                        path={mdiThumbUpOutline}
                                        size={0.8}
                                      />
                                    </button>
                                  ) : (
                                    <button
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: theme.palette.error.main,
                                      }}
                                    >
                                      <Icon
                                        path={mdiThumbDownOutline}
                                        size={0.8}
                                      />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <p>Käyttäjä ei antanut palautetta</p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
